import { useCallback, useEffect, useState, CSSProperties, ReactNode } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router';
import { i18n } from 'app/utils/i18n';
import { createNewDraft } from 'app/utils/draft';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { usePermissions } from 'app/utils/hooks/document-permission';
import { draftQueryKey, NO_DRAFT } from 'app/api/draft-queries';
import { useDesignDraft } from './hooks/useDesignDraft';
import { useResetOnDraftDiscard } from './hooks/useResetOnDraftDiscard';
import { lintCustomCss, wrapDisplayForVisibilityToggle } from './utils/scss-formatting';
import { BUILTIN_SELECTOR_NAMES, BuiltinSelectorName, HoveredTarget, getVisibilityVar } from './utils/builtin-selectors';
import { registerPendingDesignDraftSave } from './utils/pending-design-saves';

type SlotMap = Partial<Record<BuiltinSelectorName, string>>;

type ChainSegment = { type: 'chip' | 'tag' | 'text'; value: string } | { type: 'branch-chips'; values: readonly string[] };

type StructureChain = readonly ChainSegment[];

interface SlotMeta {
  labelKey: string;
  chains?: readonly StructureChain[];
}

const HEADER_FOOTER_CHAINS: readonly StructureChain[] = [
  [
    { type: 'chip', value: 'subarea-headers' },
    { type: 'tag', value: 'ol' },
    { type: 'tag', value: 'li' },
    { type: 'tag', value: 'a' },
  ],
  [{ type: 'chip', value: 'subarea-pagenumber' }],
  [{ type: 'chip', value: 'subarea-texts' }],
];

const SLOT_META: Record<BuiltinSelectorName, SlotMeta> = {
  'area-header': {
    labelKey: 'dc.builtin.label-header',
    chains: HEADER_FOOTER_CHAINS,
  },
  'area-footer': {
    labelKey: 'dc.builtin.label-footer',
    chains: HEADER_FOOTER_CHAINS,
  },
  'editor-table-of-content--container': {
    labelKey: 'dc.builtin.label-toc',
    chains: [
      [
        { type: 'chip', value: 'editor-table-of-content--content' },
        { type: 'tag', value: 'ol' },
        { type: 'tag', value: 'li' },
        { type: 'chip', value: 'toc-item' },
        { type: 'tag', value: 'a' },
        { type: 'branch-chips', values: ['toc-header', 'toc-pagenumber'] },
      ],
    ],
  },
};

const PLACEHOLDER: Record<BuiltinSelectorName, string> = {
  'area-header': 'display: var(--xrpShowHeader, flex);\nalign-items: center;',
  'area-footer': 'display: var(--xrpShowFooter, flex);\njustify-content: space-between;',
  'editor-table-of-content--container': '& ol {\n  & li {\n    /* … */\n  }\n}',
};

interface UseBuiltinCssSlotsOptions {
  onInnerHover?: (hovered: HoveredTarget | null) => void;
}

export function useBuiltinCssSlots(options: UseBuiltinCssSlotsOptions = {}) {
  const { onInnerHover } = options;
  const { documentId } = useParams<{ documentId: string }>();
  const { documentPermissions } = usePermissions(documentId);
  const isCoder = documentPermissions.includes('CODER');

  const queryClient = useQueryClient();
  const { pDocument, style, draft, saveDraft } = useDesignDraft({ styleType: 'form-classes', parsed: true });

  const publishedSlots: SlotMap = (style.data as any)?.builtinSlots ?? {};
  const draftEntry = draft.data?.content?.find((d: any) => d.type === 'builtin-css') as { slots?: SlotMap } | undefined;
  const draftSlots: SlotMap = draftEntry?.slots ?? {};

  const [stateSlots, setStateSlots] = useState<SlotMap | null>(null);
  const [errors, setErrors] = useState<Partial<Record<BuiltinSelectorName, string>>>({});

  useResetOnDraftDiscard(
    draft.data,
    useCallback(() => {
      setStateSlots(null);
      setErrors({});
    }, [])
  );

  const draftHasFormClasses = !!draft.data?.content?.some?.((d: any) => d.type === 'form-classes');
  const merged: SlotMap = stateSlots ?? (draftHasFormClasses ? draftSlots : publishedSlots);

  const [pushDraft, cancelPushDraft] = useDelayedSave((next: SlotMap) => {
    const cleaned: SlotMap = {};
    const newErrors: Partial<Record<BuiltinSelectorName, string>> = {};

    for (const name of BUILTIN_SELECTOR_NAMES) {
      const raw = (next[name] ?? '').trim();
      if (!raw) continue;
      const wrapped = wrapDisplayForVisibilityToggle(raw, getVisibilityVar(name));
      const lint = lintCustomCss(wrapped);
      if (!lint.balanced) {
        newErrors[name] = i18n('dc.modal.css-unbalanced');
        continue;
      }
      cleaned[name] = lint.output;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const entry: any = { type: 'builtin-css', slots: cleaned };
    const latestDraft = queryClient.getQueryData(draftQueryKey(documentId)) ?? NO_DRAFT;
    const newDraft = createNewDraft(pDocument, latestDraft, entry);
    saveDraft(newDraft, {
      onSuccess: () => {
        setStateSlots(cleaned);
      },
    });
  }, 1000);
  useEffect(() => registerPendingDesignDraftSave(cancelPushDraft), [cancelPushDraft]);

  const handleChange = (name: BuiltinSelectorName, value: string) => {
    const next: SlotMap = { ...merged, [name]: value };
    setStateSlots(next);
    if (errors[name]) {
      setErrors((prev) => {
        const { [name]: _omit, ...rest } = prev;
        return rest;
      });
    }
    pushDraft(next);
  };

  const renderSlot = (name: BuiltinSelectorName): ReactNode => {
    if (!isCoder) return null;
    return (
      <BuiltinCssSlot
        key={name}
        name={name}
        value={merged[name] ?? ''}
        error={errors[name]}
        onChange={(v) => handleChange(name, v)}
        onInnerHover={onInnerHover}
      />
    );
  };

  return { isCoder, renderSlot };
}

interface BuiltinCssSlotProps {
  name: BuiltinSelectorName;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  onInnerHover?: (hovered: HoveredTarget | null) => void;
}

function BuiltinCssSlot({ name, value, error, onChange, onInnerHover }: BuiltinCssSlotProps) {
  const meta = SLOT_META[name];
  return (
    <div className="builtin-css-slot mt-3 mb-3">
      <h6 className="fs-7 text-uppercase text-muted mb-1">{i18n('dc.builtin.title')}</h6>
      <p className="text-muted fs-7 mb-2">{i18n('dc.builtin.help')}</p>
      <label className="form-label fs-7 fw-semibold mb-1">
        <code style={{ color: '#547ba2' }}>.{name}</code>
        <span className="text-muted ms-2">{i18n(meta.labelKey)}</span>
      </label>
      {meta.chains && (
        <div className="dc-slot-structure mb-2">
          {meta.chains.map((chain, ci) => (
            <div key={ci} className="dc-slot-structure__chain">
              {chain.map((seg, si) => (
                <span key={si}>
                  {si > 0 && <span className="dc-slot-structure__separator"> {'>'} </span>}
                  {seg.type === 'chip' ? (
                    <code
                      className="dc-class-chip"
                      style={{ color: '#547ba2' }}
                      onMouseEnter={() => onInnerHover?.({ slot: name, kind: 'class', value: seg.value })}
                      onMouseLeave={() => onInnerHover?.(null)}
                    >
                      .{seg.value}
                    </code>
                  ) : seg.type === 'tag' ? (
                    <code
                      className="dc-class-chip dc-slot-structure__tag"
                      onMouseEnter={() => onInnerHover?.({ slot: name, kind: 'tag', value: seg.value })}
                      onMouseLeave={() => onInnerHover?.(null)}
                    >
                      {seg.value}
                    </code>
                  ) : seg.type === 'branch-chips' ? (
                    seg.values.map((v, vi) => (
                      <span key={v}>
                        {vi > 0 && ' '}
                        <code
                          className="dc-class-chip"
                          style={{ color: '#547ba2' }}
                          onMouseEnter={() => onInnerHover?.({ slot: name, kind: 'class', value: v })}
                          onMouseLeave={() => onInnerHover?.(null)}
                        >
                          .{v}
                        </code>
                      </span>
                    ))
                  ) : (
                    <code className="dc-slot-structure__tag">{seg.value}</code>
                  )}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
      {error && (
        <div className="alert alert-danger py-2 fs-7">
          <i className="fa-regular fa-circle-exclamation me-1" />
          {error}
        </div>
      )}
      <textarea
        className={`form-control form-control-sm ${error ? 'is-invalid' : ''}`}
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={PLACEHOLDER[name]}
        style={
          {
            fontFamily: 'ui-monospace, monospace',
            fontSize: '0.8125rem',
            lineHeight: 1.5,
            tabSize: 2,
            resize: 'none',
            minHeight: '8em',
            fieldSizing: 'content',
          } as CSSProperties
        }
      />
    </div>
  );
}
